import { useState } from 'react';
import { Banner, Button, Modal, Radio } from '@immo/ui-kit';
import { SANDBOX_RESET_DATA_KINDS, type SandboxResetDataKind, type SandboxResetRun } from '@immo/api-contract';
import { apiClient } from '@/lib/auth/api-client';
import { useTranslations } from '@/i18n/use-translations';
import { AlertCircleIcon, WarningTriangleIcon } from '@/components/layout/icons';

type Props = {
  readonly defaultDataKind: SandboxResetDataKind;
  readonly onClose: () => void;
  readonly onStarted: (run: SandboxResetRun) => void;
  readonly onRefused: () => void;
};

type State = { readonly kind: 'idle' | 'sending' | 'refused' | 'failed' };

const RADIO_GROUP = 'sandbox-reset-data-kind';

export function SandboxResetDialog({ defaultDataKind, onClose, onStarted, onRefused }: Props) {
  const t = useTranslations('pages.admin.sandbox');
  const [dataKind, setDataKind] = useState<SandboxResetDataKind>(defaultDataKind);
  const [state, setState] = useState<State>({ kind: 'idle' });
  const sending = state.kind === 'sending';
  const refused = state.kind === 'refused';

  async function submit() {
    if (sending) return;

    setState({ kind: 'sending' });

    try {
      const result = await apiClient.adminSandbox.startReset({ body: { dataKind } });

      if (!result.ok) {
        setState({ kind: 'failed' });

        return;
      }

      if (result.body.outcome === 'STARTED') {
        onStarted(result.body.run);

        return;
      }

      setState({ kind: 'refused' });
      onRefused();
    } catch {
      setState({ kind: 'failed' });
    }
  }

  return (
    <Modal
      open
      size="md"
      confirmOnClose={false}
      closeDisabled={sending}
      onClose={onClose}
      title={t('dialog.title')}
      description={t('dialog.body')}
      footer={
        <>
          <Button variant="secondary" type="button" onClick={onClose} disabled={sending}>
            {t('dialog.cancel')}
          </Button>
          <Button
            variant="danger"
            type="button"
            onClick={() => void submit()}
            loading={sending}
            disabled={sending || refused}
          >
            {t('dialog.confirm')}
          </Button>
        </>
      }
    >
      <fieldset className="flex flex-col gap-3">
        <legend className="mb-2 text-xs text-text-tertiary">{t('dialog.choice-label')}</legend>
        {SANDBOX_RESET_DATA_KINDS.map((kind) => (
          <label key={kind} className="flex items-start gap-2 text-sm text-text-primary">
            <Radio
              name={RADIO_GROUP}
              value={kind}
              checked={dataKind === kind}
              onSelect={() => setDataKind(kind)}
              disabled={sending}
              className="mt-0.5"
            />
            <span className="flex flex-col">
              {t(`dialog.options.${kind}.label`)}
              <span className="text-text-secondary">{t(`dialog.options.${kind}.hint`)}</span>
            </span>
          </label>
        ))}
      </fieldset>
      {refused && (
        <div className="mt-4">
          <Banner variant="warning" icon={<WarningTriangleIcon />} title={t('dialog.busy-refusal')} />
        </div>
      )}
      {state.kind === 'failed' && (
        <div className="mt-4">
          <Banner variant="danger" icon={<AlertCircleIcon />} title={t('dialog.start-failed')} />
        </div>
      )}
    </Modal>
  );
}
